import { useCallback, useEffect, useRef, useState } from 'react';

import { HttpStatusError, isUnauthorizedError } from '@/lib/http-status-error';
import { logger } from '@/lib/logger';

/**
 * WebSocket接続状態
 */
export type ConnectionState = 'disconnected' | 'connecting' | 'connected' | 'error';

/**
 * 文字起こしWebSocket接続フックの戻り値
 */
export interface UseTranscriptionConnectionReturn {
  /** WebSocket接続を開始する */
  connect: (params?: ConnectParams) => Promise<string>;
  /** WebSocket接続を切断する */
  disconnect: () => void;
  /** 音声チャンクを送信する */
  sendChunk: (chunk: Blob, chunkSeq?: number) => Promise<void>;
  /** フォーマット要求を送信する */
  requestFormat: () => Promise<void>;
  /** S3保存要求を送信する（$finalルート） */
  requestFinal: () => Promise<void>;
  /** 文字起こし結果を取得する（$defaultルート） */
  requestTranscript: () => Promise<void>;
  /** ポーリングを開始する */
  startPolling: () => void;
  /** ポーリングを停止する */
  stopPolling: () => void;
  /** 接続状態 */
  connectionState: ConnectionState;
  /** 現在の文字起こしテキスト */
  transcript: string;
  /** エラー情報 */
  error: Error | null;
  /** 接続ID */
  connectionId: string | null;
  /** ミーティングID */
  meetingId: string | null;
  /** 最終チャンク送信時刻 */
  lastChunkSentAt: number | null;
  /** 最終文字起こし更新時刻 */
  lastTranscriptAt: number | null;
  /** 最終$defaultレスポンス受信時刻 */
  lastDefaultResponseAt: number | null;
  /** rawチャンネルの文字起こしテキスト（リアルタイム更新） */
  rawChannelText: string;
  /** formattedチャンネルの文字起こしテキスト（リアルタイム更新） */
  formattedChannelText: string;
  /** 確定済みテキスト（commit state / v2） */
  committedText: string;
  /** 暫定テキスト（commit state / v2） */
  draftText: string;
  /** commit state のリビジョン（commit/replaceの単位で増える） */
  revision: number | null;
  /** 集約パイプライン状態（Lane A/B, segment summary など） */
  pipelineState: TranscriptionPipelineState | null;
}

export type ConnectParams =
  | string
  | {
      meetingId?: string;
      workspaceId?: string;
      resume?: boolean;
      connectionOnly?: boolean;
    };

/**
 * 文字起こしWebSocket接続フックのオプション
 */
export interface UseTranscriptionConnectionOptions {
  /** WebSocketエンドポイントURL */
  wsUrl?: string;
  /** 文字起こし結果を受信したときのコールバック */
  onTranscriptReceived?: (text: string) => void;
  /** 生の文字起こしを受信したときのコールバック */
  onRawTranscriptReceived?: (text: string) => void;
  /** Gemini整形済みの文字起こしを受信したときのコールバック */
  onFormattedTranscriptReceived?: (text: string) => void;
  /** commit state（確定/暫定/リビジョン）を受信したときのコールバック */
  onCommitStateReceived?: (state: { revision: number; committedText: string; draftText: string }) => void;
  /** speech2text のバージョン情報を受信したときのコールバック */
  onSpeech2textInfo?: (info: Speech2textInfo) => void;
  /** 集約パイプライン状態を受信したときのコールバック */
  onPipelineStateReceived?: (state: TranscriptionPipelineState) => void;
  /** 録音単位の更新イベントを受信したときのコールバック */
  onTranscriptionEvent?: (event: TranscriptionRealtimeEvent) => void;
  /** ワークスペース単位の更新イベントを受信したときのコールバック */
  onWorkspaceTranscriptionUpdated?: (event: WorkspaceTranscriptionUpdateEvent) => void;
  /** Recall.ai リアルタイム文字起こしを受信したときのコールバック */
  onRecallTranscriptReceived?: (event: RecallTranscriptEvent) => void;
  /** Recall.ai ボットステータス変更を受信したときのコールバック */
  onRecallBotStatusChanged?: (event: RecallBotStatusEvent) => void;
  /** 接続確立時のコールバック */
  onConnected?: (connectionId: string, meetingId: string) => void;
  /** 接続切断時のコールバック */
  onDisconnected?: () => void;
  /** WebSocket close 時に発火。code/reason/wasClean を含む。 */
  onClose?: (event: { code: number; reason: string; wasClean: boolean }) => void;
  /** エラー発生時のコールバック */
  onError?: (error: Error) => void;
}

export interface Speech2textInfo {
  version?: string;
  gitSha?: string;
  deployTime?: string;
}

export interface TranscriptionPipelineSegmentSummary {
  id?: number | null;
  start_seq?: number | null;
  end_seq?: number | null;
  chunk_length?: number | null;
  estimated_ms?: number | null;
  text_length?: number | null;
  b_revision?: number | null;
}

export interface TranscriptionPipelineState {
  ts_ms?: number;
  speech2text?: Speech2textInfo;
  lane_a?: {
    enabled?: boolean;
    pcm_only?: boolean;
    chunk_ms?: number;
    target_ms?: number;
    max_wait_ms?: number;
    max_window_chunks?: number;
    merged_last_seq?: number;
    last_flushed_seq?: number;
    pending_chunks?: number;
    pending_ms?: number;
    pending_estimated_ms?: number;
    next_window_start_seq?: number | null;
    next_window_end_seq?: number | null;
    lock_in_flight?: boolean;
  };
  lane_b?: {
    queue_mode?: string;
    lock_in_flight?: boolean;
    last_refined_segment_id?: number;
    last_refined_revision?: number;
    last_refined_at_ms?: number;
    last_window_start_seq?: number;
    last_window_end_seq?: number;
    last_gemini_status?: string | null;
    last_gemini_applied?: boolean | null;
    last_gemini_confidence?: number | null;
    last_gemini_did_normalize?: boolean | null;
    last_gemini_did_remove_overlap?: boolean | null;
    last_overlap_trim_applied?: boolean | null;
    last_overlap_trimmed_chars?: number;
    last_overlap_candidate_len?: number;
    last_overlap_result_len?: number;
  };
  segments?: {
    count?: number;
    stable_count?: number;
    unstable_count?: number;
    latest?: TranscriptionPipelineSegmentSummary | null;
  };
  commit?: {
    revision?: number;
    committed_length?: number;
    stable_committed_length?: number;
    unstable_committed_length?: number;
    stable_end_seq?: number | null;
    draft_length?: number;
    draft_age_ms?: number;
  };
}

export interface TranscriptionRealtimeEvent {
  type: string;
  meetingId: string;
  status?: string;
  formattedAvailable?: boolean;
  minutesStatus?: 'none' | 'processing' | 'completed' | 'failed';
  tsMs?: number;
}

export interface WorkspaceTranscriptionUpdateEvent {
  type: 'workspace.transcription.updated';
  workspaceId: string;
  meetingId: string;
  status?: string;
  formattedAvailable?: boolean;
  minutesStatus?: 'none' | 'processing' | 'completed' | 'failed';
  source?: string;
  tsMs?: number;
}

export interface RecallTranscriptEvent {
  type: 'recall.transcript';
  meetingId: string;
  speaker: string;
  text: string;
  isFinal: boolean;
  tsMs: number;
}

export interface RecallBotStatusEvent {
  type: 'recall.bot_status';
  meetingId: string;
  botId: string;
  status: string;
  recordingState: string;
  tsMs: number;
}

/**
 * WebSocketメッセージ型定義
 */
interface WebSocketMessage {
  action: string;
  connectionId?: string;
  meeting_id?: string; // 送信用（snake_case）
  data?: Record<string, unknown>;
  type?: string;
  text?: string;
  // transcript.delta 用
  delta?: string;
  chunk_seq?: number;
  error?: string;
  // $connect レスポンス用（camelCase）
  message?: string;
  meetingId?: string;
  userId?: string;
  email?: string;
}

type UnknownRecord = Record<string, unknown>;

type MinutesStatus = WorkspaceTranscriptionUpdateEvent['minutesStatus'];

// WebSocket契約の内部表現は snake_case に統一し、受信時だけ camelCase 互換を吸収する。
const asRecord = (value: unknown): UnknownRecord | null => {
  if (!value || typeof value !== 'object') {
    return null;
  }
  return value as UnknownRecord;
};

const readAliased = (record: UnknownRecord, snakeKey: string, camelKey: string): unknown => {
  if (snakeKey in record) {
    return record[snakeKey];
  }
  if (camelKey in record) {
    return record[camelKey];
  }
  return undefined;
};

const readAliasedString = (record: UnknownRecord, snakeKey: string, camelKey: string): string | undefined => {
  const value = readAliased(record, snakeKey, camelKey);
  if (typeof value !== 'string') {
    return undefined;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
};

const readAliasedBoolean = (record: UnknownRecord, snakeKey: string, camelKey: string): boolean | undefined => {
  const value = readAliased(record, snakeKey, camelKey);
  return typeof value === 'boolean' ? value : undefined;
};

const parseMinutesStatus = (value: unknown): MinutesStatus => {
  if (value === 'processing' || value === 'completed' || value === 'failed' || value === 'none') {
    return value;
  }
  return undefined;
};

const parseOptionalNumber = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return null;
};

const readMeetingId = (record: UnknownRecord): string | undefined => {
  return readAliasedString(record, 'meeting_id', 'meetingId');
};

const readWorkspaceId = (record: UnknownRecord): string | undefined => {
  return readAliasedString(record, 'workspace_id', 'workspaceId');
};

const readTsMs = (record: UnknownRecord): number | undefined => {
  return parseOptionalNumber(readAliased(record, 'ts_ms', 'tsMs')) ?? undefined;
};

const hasNonWhitespace = (value: string): boolean => {
  return value.trim().length > 0;
};

const pickLongerText = (current: string, candidate: string): string => {
  if (!candidate) {
    return current;
  }
  if (!current) {
    return candidate;
  }
  return candidate.length > current.length ? candidate : current;
};

const extractSpeech2textInfo = (body: UnknownRecord): Speech2textInfo | null => {
  const speech2text = asRecord(body.speech2text);
  if (!speech2text) {
    return null;
  }

  const info: Speech2textInfo = {
    version: readAliasedString(speech2text, 'version', 'version'),
    gitSha: readAliasedString(speech2text, 'git_sha', 'gitSha'),
    deployTime: readAliasedString(speech2text, 'deploy_time', 'deployTime'),
  };

  return info.version || info.gitSha || info.deployTime ? info : null;
};

const selectPipelineStateCandidate = (body: UnknownRecord): UnknownRecord | null => {
  const directCandidate = asRecord(readAliased(body, 'pipeline_state', 'pipelineState'));
  if (directCandidate) {
    return directCandidate;
  }

  const dataRecord = asRecord(body.data);
  const dataCandidate = dataRecord ? asRecord(readAliased(dataRecord, 'pipeline_state', 'pipelineState')) : null;
  if (dataCandidate) {
    return dataCandidate;
  }

  const hasRootLaneState =
    asRecord(readAliased(body, 'lane_a', 'laneA')) || asRecord(readAliased(body, 'lane_b', 'laneB'));
  return hasRootLaneState ? body : null;
};

const buildPipelineState = (candidate: UnknownRecord): TranscriptionPipelineState => {
  const speech2text = asRecord(candidate.speech2text);
  const laneA = asRecord(readAliased(candidate, 'lane_a', 'laneA'));
  const laneB = asRecord(readAliased(candidate, 'lane_b', 'laneB'));
  const segments = asRecord(candidate.segments);
  const commit = asRecord(candidate.commit);
  const tsMs = parseOptionalNumber(readAliased(candidate, 'ts_ms', 'tsMs'));

  return {
    ...(tsMs !== null ? { ts_ms: tsMs } : {}),
    ...(speech2text ? { speech2text: speech2text as Speech2textInfo } : {}),
    ...(laneA ? { lane_a: laneA as TranscriptionPipelineState['lane_a'] } : {}),
    ...(laneB ? { lane_b: laneB as TranscriptionPipelineState['lane_b'] } : {}),
    ...(segments ? { segments: segments as TranscriptionPipelineState['segments'] } : {}),
    ...(commit ? { commit: commit as TranscriptionPipelineState['commit'] } : {}),
  };
};

type LifecycleMessageType =
  | 'transcript.finalized'
  | 'transcript.formatted'
  | 'transcript.completed'
  | 'transcript.processing'
  | 'transcript.failed';

const isLifecycleMessageType = (value: string): value is LifecycleMessageType => {
  return (
    value === 'transcript.finalized' ||
    value === 'transcript.formatted' ||
    value === 'transcript.completed' ||
    value === 'transcript.processing' ||
    value === 'transcript.failed'
  );
};

const isSnapshotCandidate = (bodyCandidate: UnknownRecord): boolean => {
  return (
    typeof readAliased(bodyCandidate, 'raw_transcription', 'rawTranscription') === 'string' ||
    typeof readAliased(bodyCandidate, 'formatted_transcription', 'formattedTranscription') === 'string' ||
    typeof bodyCandidate.speech2text === 'object'
  );
};

const readSocketPayloadText = async (data: unknown): Promise<string> => {
  if (typeof data === 'string') {
    return data;
  }
  if (data instanceof Blob) {
    return data.text();
  }
  if (data instanceof ArrayBuffer) {
    return new TextDecoder('utf-8').decode(data);
  }
  return String(data ?? '');
};

const blobToBase64 = async (chunk: Blob): Promise<string> => {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result as string;
      const base64 = result.split(',')[1] ?? '';
      logger.info('[useTranscriptionConnection] Base64 encoded', {
        base64Length: base64.length,
        prefix: result.substring(0, 30),
      });
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(chunk);
  });
};

interface AudioChunkMetadata {
  isWav: boolean;
  isWebm: boolean;
  isPcm: boolean;
  pcmSampleRate: number;
  pcmChannels: number;
  detectedFormat: 'PCM16' | 'WAV' | 'WebM' | 'Unknown';
}

const getAudioChunkMetadata = (contentType: string): AudioChunkMetadata => {
  const isWav = contentType.includes('wav') || contentType.includes('audio/wav');
  const isWebm = contentType.includes('webm') || contentType.includes('audio/webm');
  const isPcm = contentType.includes('audio/pcm');
  const rateMatch = contentType.match(/rate=(\d+)/);
  const channelsMatch = contentType.match(/channels=(\d+)/);
  const pcmSampleRate = rateMatch ? Number(rateMatch[1]) : 16000;
  const pcmChannels = channelsMatch ? Number(channelsMatch[1]) : 1;

  return {
    isWav,
    isWebm,
    isPcm,
    pcmSampleRate,
    pcmChannels,
    detectedFormat: isPcm ? 'PCM16' : isWav ? 'WAV' : isWebm ? 'WebM' : 'Unknown',
  };
};

const buildChunkPcmData = (audioMetadata: AudioChunkMetadata): Record<string, unknown> => {
  if (!audioMetadata.isPcm) {
    return {};
  }
  return {
    audioFormat: 'pcm16le',
    sampleRate: Number.isFinite(audioMetadata.pcmSampleRate) ? audioMetadata.pcmSampleRate : 16000,
    channels: Number.isFinite(audioMetadata.pcmChannels) ? audioMetadata.pcmChannels : 1,
  };
};

type SnapshotBodyFields = {
  snapshotMeetingId: string | undefined;
  revision: number | null;
  committed: string;
  draft: string;
  formattedResolved: string;
  rawResolved: string;
};

const readSnapshotBodyFields = (body: UnknownRecord): SnapshotBodyFields => {
  const formatted = readAliasedString(body, 'formatted_transcription', 'formattedTranscription') ?? '';
  const raw = readAliasedString(body, 'raw_transcription', 'rawTranscription') ?? '';

  return {
    snapshotMeetingId: readMeetingId(body),
    revision: parseOptionalNumber(body.revision),
    committed: readAliasedString(body, 'committed_text', 'committedText') ?? '',
    draft: readAliasedString(body, 'draft_text', 'draftText') ?? '',
    formattedResolved: hasNonWhitespace(formatted) ? formatted : '',
    rawResolved: hasNonWhitespace(raw) ? raw : '',
  };
};

const resolveConnectOptions = (params?: ConnectParams) => {
  const connectOptions = typeof params === 'string' ? { meetingId: params } : (params ?? {});
  const providedMeetingId = typeof connectOptions.meetingId === 'string' ? connectOptions.meetingId.trim() : '';
  const providedWorkspaceId =
    typeof connectOptions.workspaceId === 'string' ? connectOptions.workspaceId.trim() : '';
  const connectionOnly = Boolean(connectOptions.connectionOnly);
  const generatedMeetingId = connectionOnly ? null : providedMeetingId || crypto.randomUUID();
  const resumeRequested = connectOptions.resume ?? Boolean(providedMeetingId);
  const resolvedConnectionId = generatedMeetingId ?? `conn-${crypto.randomUUID()}`;

  return {
    connectionOnly,
    generatedMeetingId,
    providedMeetingId,
    providedWorkspaceId,
    resolvedConnectionId,
    resumeRequested,
  };
};

/**
 * 文字起こし用WebSocket接続を管理するカスタムフック
 *
 * Lambda側のルートキー:
 * - $connect: 接続確立（自動）
 * - $disconnect: 切断（自動）
 * - transcribe_chunk: 音声チャンク送信
 * - default: 文字起こし結果受信
 * - format: フォーマット要求
 *
 * @param options - コールバックオプション
 * @returns WebSocket接続管理機能
 *
 * @example
 * ```tsx
 * const {
 *   connect,
 *   disconnect,
 *   sendChunk,
 *   requestFormat,
 *   connectionState,
 *   transcript,
 *   error
 * } = useTranscriptionConnection({
 *   wsUrl: 'wss://example.com/transcribe',
 *   onTranscriptReceived: (text) => console.log('Transcript:', text),
 *   onConnected: (id) => console.log('Connected:', id),
 * });
 *
 * // 接続開始
 * const connId = await connect();
 *
 * // 音声チャンク送信
 * await sendChunk(audioBlob);
 *
 * // フォーマット要求
 * await requestFormat();
 *
 * // 切断
 * disconnect();
 * ```
 */
export function useTranscriptionConnection(
  options: UseTranscriptionConnectionOptions = {},
): UseTranscriptionConnectionReturn {
  const {
    wsUrl,
    onTranscriptReceived,
    onRawTranscriptReceived,
    onFormattedTranscriptReceived,
    onCommitStateReceived,
    onSpeech2textInfo,
    onPipelineStateReceived,
    onTranscriptionEvent,
    onWorkspaceTranscriptionUpdated,
    onRecallTranscriptReceived,
    onRecallBotStatusChanged,
    onConnected,
    onDisconnected,
    onClose,
    onError,
  } = options;

  const [connectionState, setConnectionState] = useState<ConnectionState>('disconnected');
  const [transcript, setTranscript] = useState<string>('');
  const transcriptRef = useRef(transcript);
  const [rawChannelText, setRawChannelText] = useState<string>('');
  const [formattedChannelText, setFormattedChannelText] = useState<string>('');
  const [committedText, setCommittedText] = useState<string>('');
  const [draftText, setDraftText] = useState<string>('');
  const [revision, setRevision] = useState<number | null>(null);
  const [pipelineState, setPipelineState] = useState<TranscriptionPipelineState | null>(null);
  const committedTextRef = useRef<string>('');
  const draftTextRef = useRef<string>('');
  const revisionRef = useRef<number | null>(null);
  const rawTranscriptRef = useRef<string>('');
  const formattedTranscriptRef = useRef<string>('');
  const [error, setError] = useState<Error | null>(null);
  const [connectionId, setConnectionId] = useState<string | null>(null);
  const [meetingId, setMeetingId] = useState<string | null>(null);
  const [lastChunkSentAt, setLastChunkSentAt] = useState<number | null>(null);
  const [lastTranscriptAt, setLastTranscriptAt] = useState<number | null>(null);
  const [lastDefaultResponseAt, setLastDefaultResponseAt] = useState<number | null>(null);
  const lastSnapshotRevisionRef = useRef<number>(-1);
  const textLastSeqRef = useRef<number>(-1);
  const pendingDeltaBySeqRef = useRef<Map<number, string>>(new Map());
  const snapshotCommittedRef = useRef<string>('');
  const snapshotDraftRef = useRef<string>('');
  const hasSeqOwnershipSnapshotRef = useRef<boolean>(false);

  const wsRef = useRef<WebSocket | null>(null);
  const meetingIdRef = useRef<string | null>(null);
  // 同一接続内での final 二重送信を防ぐためのフラグ。connect() ごとにリセット。
  const finalSentRef = useRef<boolean>(false);
  const onTranscriptReceivedRef = useRef(onTranscriptReceived);
  const onRawTranscriptReceivedRef = useRef(onRawTranscriptReceived);
  const onFormattedTranscriptReceivedRef = useRef(onFormattedTranscriptReceived);
  const onCommitStateReceivedRef = useRef(onCommitStateReceived);
  const onSpeech2textInfoRef = useRef(onSpeech2textInfo);
  const onPipelineStateReceivedRef = useRef(onPipelineStateReceived);
  const onTranscriptionEventRef = useRef(onTranscriptionEvent);
  const onWorkspaceTranscriptionUpdatedRef = useRef(onWorkspaceTranscriptionUpdated);
  const onRecallTranscriptReceivedRef = useRef(onRecallTranscriptReceived);
  const onRecallBotStatusChangedRef = useRef(onRecallBotStatusChanged);
  const onConnectedRef = useRef(onConnected);
  const onDisconnectedRef = useRef(onDisconnected);
  const onCloseRef = useRef(onClose);
  const onErrorRef = useRef(onError);

  // ポーリング管理用のRef
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isPollingRef = useRef<boolean>(false);

  // コールバックのrefを最新に保つ
  useEffect(() => {
    onTranscriptReceivedRef.current = onTranscriptReceived;
    onRawTranscriptReceivedRef.current = onRawTranscriptReceived;
    onFormattedTranscriptReceivedRef.current = onFormattedTranscriptReceived;
    onCommitStateReceivedRef.current = onCommitStateReceived;
    onSpeech2textInfoRef.current = onSpeech2textInfo;
    onPipelineStateReceivedRef.current = onPipelineStateReceived;
    onTranscriptionEventRef.current = onTranscriptionEvent;
    onWorkspaceTranscriptionUpdatedRef.current = onWorkspaceTranscriptionUpdated;
    onRecallTranscriptReceivedRef.current = onRecallTranscriptReceived;
    onRecallBotStatusChangedRef.current = onRecallBotStatusChanged;
    onConnectedRef.current = onConnected;
    onDisconnectedRef.current = onDisconnected;
    onCloseRef.current = onClose;
    onErrorRef.current = onError;
  }, [
    onTranscriptReceived,
    onRawTranscriptReceived,
    onFormattedTranscriptReceived,
    onCommitStateReceived,
    onSpeech2textInfo,
    onPipelineStateReceived,
    onTranscriptionEvent,
    onWorkspaceTranscriptionUpdated,
    onRecallTranscriptReceived,
    onRecallBotStatusChanged,
    onConnected,
    onDisconnected,
    onClose,
    onError,
  ]);

  const buildPendingTailFromSeq = useCallback((ackedSeq: number): string => {
    const entries = Array.from(pendingDeltaBySeqRef.current.entries())
      .filter(([seq]) => seq > ackedSeq)
      .sort((a, b) => a[0] - b[0]);
    return entries.map(([, text]) => text).join('');
  }, []);

  const prunePendingDelta = useCallback((ackedSeq: number) => {
    if (!Number.isFinite(ackedSeq)) {
      return;
    }
    for (const seq of Array.from(pendingDeltaBySeqRef.current.keys())) {
      if (seq <= ackedSeq) {
        pendingDeltaBySeqRef.current.delete(seq);
      }
    }
  }, []);

  const handlePipelineState = useCallback((body: UnknownRecord) => {
    const candidate = selectPipelineStateCandidate(body);
    if (!candidate) {
      return;
    }

    const nextState = buildPipelineState(candidate);
    setPipelineState(nextState);
    onPipelineStateReceivedRef.current?.(nextState);
  }, []);

  useEffect(() => {
    transcriptRef.current = transcript;
  }, [transcript]);

  useEffect(() => {
    committedTextRef.current = committedText;
    draftTextRef.current = draftText;
    revisionRef.current = revision;
  }, [committedText, draftText, revision]);

  const applyTranscriptUpdate = useCallback((nextText: string, source: 'raw' | 'formatted' | 'legacy') => {
    if (!nextText) {
      return;
    }

    const prev = transcriptRef.current;
    const shouldRejectShorter = source !== 'legacy' && !!prev && nextText.length < prev.length;
    if (shouldRejectShorter || nextText === prev) {
      return;
    }

    transcriptRef.current = nextText;
    setTranscript(nextText);
    setLastTranscriptAt(Date.now());
    onTranscriptReceivedRef.current?.(nextText);
    logger.debug('[useTranscriptionConnection] Transcript updated from', {
      source,
      length: nextText.length,
    });
  }, []);

  const syncChannelTranscript = useCallback(
    (nextText: string, channel: 'raw' | 'formatted', allowShorter = false) => {
      if (!nextText) {
        return;
      }

      const ref = channel === 'raw' ? rawTranscriptRef : formattedTranscriptRef;
      const prev = ref.current;
      if ((!allowShorter && !!prev && nextText.length < prev.length) || nextText === prev) {
        return;
      }

      ref.current = nextText;

      if (channel === 'raw') {
        setRawChannelText(nextText);
        onRawTranscriptReceivedRef.current?.(nextText);
        return;
      }

      setFormattedChannelText(nextText);
      onFormattedTranscriptReceivedRef.current?.(nextText);
    },
    [],
  );

  const applyChannelTranscriptUpdate = useCallback((nextText: string, channel: 'raw' | 'formatted') => {
    syncChannelTranscript(nextText, channel);
  }, [syncChannelTranscript]);

  const applyChannelTranscriptUpdateForce = useCallback((nextText: string, channel: 'raw' | 'formatted') => {
    syncChannelTranscript(nextText, channel, true);
  }, [syncChannelTranscript]);

  const handleSpeech2textInfo = useCallback((body: UnknownRecord) => {
    const info = extractSpeech2textInfo(body);
    if (info) {
      onSpeech2textInfoRef.current?.(info);
    }
  }, []);

  const isStaleSnapshot = useCallback((snapshotMeetingId: string | undefined, revision: number | null) => {
    if (
      revision != null &&
      revisionRef.current != null &&
      Number.isFinite(revisionRef.current) &&
      revision < revisionRef.current
    ) {
      logger.debug('[useTranscriptionConnection] Ignored stale snapshot', {
        snapshotRevision: revision,
        currentRevision: revisionRef.current,
        meetingId: snapshotMeetingId,
      });
      return true;
    }

    return false;
  }, []);

  const readSnapshotAckedSeq = useCallback((body: UnknownRecord): number | null => {
    const snapshotLastSeq = parseOptionalNumber(readAliased(body, 'last_seq', 'lastSeq'));
    const snapshotTextLastSeq = parseOptionalNumber(readAliased(body, 'text_last_seq', 'textLastSeq'));
    const snapshotDraftEndSeq = parseOptionalNumber(readAliased(body, 'draft_end_seq', 'draftEndSeq'));
    const snapshotCommittedEndSeq = parseOptionalNumber(readAliased(body, 'committed_end_seq', 'committedEndSeq'));

    return snapshotTextLastSeq ?? snapshotDraftEndSeq ?? snapshotCommittedEndSeq ?? snapshotLastSeq;
  }, []);

  const shouldIgnoreStaleSnapshotSeq = useCallback(
    (ackedSeqCandidate: number | null, revision: number | null, snapshotMeetingId: string | undefined) => {
      if (ackedSeqCandidate === null) {
        return false;
      }

      hasSeqOwnershipSnapshotRef.current = true;
      const currentRevision = revisionRef.current ?? -1;
      if (ackedSeqCandidate >= textLastSeqRef.current || (revision != null && revision > currentRevision)) {
        return false;
      }

      logger.debug('[useTranscriptionConnection] Ignored stale snapshot by seq', {
        snapshotAckedSeq: ackedSeqCandidate,
        currentAckedSeq: textLastSeqRef.current,
        snapshotRevision: revision,
        currentRevision,
        meetingId: snapshotMeetingId,
      });
      return true;
    },
    [],
  );

  const applyLegacySnapshotTail = useCallback((committed: string, draft: string): string => {
    const currentTotal = (committedTextRef.current || '') + (draftTextRef.current || '');
    const snapshotTotal = committed + draft;
    const canPreserveTail =
      currentTotal.length > snapshotTotal.length && snapshotTotal.length > 0 && currentTotal.startsWith(snapshotTotal);

    if (!canPreserveTail) {
      return draft;
    }

    return draft + currentTotal.slice(snapshotTotal.length);
  }, []);

  const resolveSnapshotDraft = useCallback(
    (committed: string, draft: string, revision: number | null, ackedSeqCandidate: number | null): string => {
      if (ackedSeqCandidate !== null) {
        const ackedSeq = Math.trunc(ackedSeqCandidate);
        textLastSeqRef.current = Math.max(textLastSeqRef.current, ackedSeq);
        prunePendingDelta(textLastSeqRef.current);
        snapshotCommittedRef.current = committed;
        snapshotDraftRef.current = draft;
        return draft + buildPendingTailFromSeq(textLastSeqRef.current);
      }

      if (revision != null && Number.isFinite(revision)) {
        return applyLegacySnapshotTail(committed, draft);
      }

      return draft;
    },
    [applyLegacySnapshotTail, buildPendingTailFromSeq, prunePendingDelta],
  );

  const commitSnapshotState = useCallback((revision: number | null, committed: string, effectiveDraft: string) => {
    if (revision == null || !Number.isFinite(revision)) {
      return;
    }

    committedTextRef.current = committed;
    draftTextRef.current = effectiveDraft;
    revisionRef.current = revision;

    setRevision(revision);
    setCommittedText(committed);
    setDraftText(effectiveDraft);
    onCommitStateReceivedRef.current?.({
      revision,
      committedText: committed,
      draftText: effectiveDraft,
    });
  }, []);

  const applySnapshotChannelUpdates = useCallback(
    (rawEffective: string, formattedResolved: string, forceUpdate: boolean) => {
      const applyUpdate = forceUpdate ? applyChannelTranscriptUpdateForce : applyChannelTranscriptUpdate;

      if (rawEffective) {
        applyUpdate(rawEffective, 'raw');
      }
      if (formattedResolved) {
        applyUpdate(formattedResolved, 'formatted');
      }
    },
    [applyChannelTranscriptUpdate, applyChannelTranscriptUpdateForce],
  );

  const logSnapshotProgress = useCallback(
    (
      snapshotMeetingId: string | undefined,
      rawEffective: string,
      formattedResolved: string,
      prevRawLen: number,
      prevFormattedLen: number,
    ) => {
      const progressed =
        (rawEffective && rawEffective.length > prevRawLen) ||
        (formattedResolved && formattedResolved.length > prevFormattedLen);

      if (!progressed) {
        return;
      }

      logger.info('[useTranscriptionConnection] Snapshot updated', {
        meetingId: snapshotMeetingId,
        rawLen: rawEffective.length,
        formattedLen: formattedResolved.length,
      });
    },
    [],
  );

  const getSnapshotRenderState = useCallback(
    (body: UnknownRecord) => {
      const snapshot = readSnapshotBodyFields(body);
      return {
        ...snapshot,
        prevRawLen: rawTranscriptRef.current.length,
        prevFormattedLen: formattedTranscriptRef.current.length,
        ackedSeqCandidate: readSnapshotAckedSeq(body),
      };
    },
    [readSnapshotAckedSeq],
  );

  const updateSnapshotRevision = useCallback((revision: number | null): boolean => {
    const forceUpdate = revision != null && revision > lastSnapshotRevisionRef.current;
    if (forceUpdate) {
      lastSnapshotRevisionRef.current = revision;
    }
    return forceUpdate;
  }, []);

  const resolveSnapshotTranscriptText = useCallback((committed: string, effectiveDraft: string, rawResolved: string) => {
    const rawFromV2 = hasNonWhitespace(committed + effectiveDraft) ? committed + effectiveDraft : '';
    const rawEffective = pickLongerText(rawFromV2, rawResolved);
    return {
      rawEffective,
      transcriptText: rawEffective,
    };
  }, []);

  const shouldSkipSnapshotBody = useCallback(
    (snapshotMeetingId: string | undefined, revision: number | null, ackedSeqCandidate: number | null) => {
      return (
        isStaleSnapshot(snapshotMeetingId, revision) ||
        shouldIgnoreStaleSnapshotSeq(ackedSeqCandidate, revision, snapshotMeetingId)
      );
    },
    [isStaleSnapshot, shouldIgnoreStaleSnapshotSeq],
  );

  const applySnapshotTranscriptState = useCallback(
    (
      committed: string,
      draft: string,
      revision: number | null,
      ackedSeqCandidate: number | null,
      rawResolved: string,
      formattedResolved: string,
      prevRawLen: number,
      prevFormattedLen: number,
      snapshotMeetingId: string | undefined,
    ) => {
      const forceUpdate = updateSnapshotRevision(revision);
      const effectiveDraft = resolveSnapshotDraft(committed, draft, revision, ackedSeqCandidate);
      commitSnapshotState(revision, committed, effectiveDraft);
      const { rawEffective, transcriptText } = resolveSnapshotTranscriptText(committed, effectiveDraft, rawResolved);
      const nextTranscript = transcriptText || formattedResolved;

      applySnapshotChannelUpdates(rawEffective, formattedResolved, forceUpdate);
      if (nextTranscript) {
        applyTranscriptUpdate(nextTranscript, forceUpdate ? 'legacy' : rawEffective ? 'raw' : 'formatted');
      }
      logSnapshotProgress(snapshotMeetingId, rawEffective, formattedResolved, prevRawLen, prevFormattedLen);
    },
    [
      applySnapshotChannelUpdates,
      applyTranscriptUpdate,
      commitSnapshotState,
      logSnapshotProgress,
      resolveSnapshotDraft,
      resolveSnapshotTranscriptText,
      updateSnapshotRevision,
    ],
  );

  const handleSnapshotBody = useCallback(
    (body: UnknownRecord) => {
      const {
        snapshotMeetingId,
        revision,
        committed,
        draft,
        formattedResolved,
        rawResolved,
        prevRawLen,
        prevFormattedLen,
        ackedSeqCandidate,
      } = getSnapshotRenderState(body);
      handleSpeech2textInfo(body);
      handlePipelineState(body);
      if (shouldSkipSnapshotBody(snapshotMeetingId, revision, ackedSeqCandidate)) {
        return;
      }
      applySnapshotTranscriptState(
        committed,
        draft,
        revision,
        ackedSeqCandidate,
        rawResolved,
        formattedResolved,
        prevRawLen,
        prevFormattedLen,
        snapshotMeetingId,
      );
    },
    [
      applySnapshotTranscriptState,
      getSnapshotRenderState,
      handlePipelineState,
      handleSpeech2textInfo,
      shouldSkipSnapshotBody,
    ],
  );

  const resetTranscriptState = useCallback(() => {
    transcriptRef.current = '';
    rawTranscriptRef.current = '';
    formattedTranscriptRef.current = '';
    committedTextRef.current = '';
    draftTextRef.current = '';
    revisionRef.current = null;
    setTranscript('');
    setRawChannelText('');
    setFormattedChannelText('');
    setCommittedText('');
    setDraftText('');
    setRevision(null);
    setPipelineState(null);
    setLastTranscriptAt(null);
    setLastDefaultResponseAt(null);
    setLastChunkSentAt(null);
    lastSnapshotRevisionRef.current = -1;
    textLastSeqRef.current = -1;
    pendingDeltaBySeqRef.current.clear();
    snapshotCommittedRef.current = '';
    snapshotDraftRef.current = '';
    hasSeqOwnershipSnapshotRef.current = false;
  }, []);

  // transcript の最新値を ref に反映（delta 追記の基準）
  useEffect(() => {
    transcriptRef.current = transcript;
  }, [transcript]);

  const reportError = useCallback((error: Error, nextState?: ConnectionState) => {
    setError(error);
    if (nextState) {
      setConnectionState(nextState);
    }
    onErrorRef.current?.(error);
  }, []);

  const emitCommitDraftUpdate = useCallback((nextDraft: string) => {
    if (nextDraft === draftTextRef.current) {
      return;
    }

    draftTextRef.current = nextDraft;
    setDraftText(nextDraft);
    const rev = revisionRef.current;
    if (typeof rev === 'number' && Number.isFinite(rev)) {
      onCommitStateReceivedRef.current?.({
        revision: rev,
        committedText: committedTextRef.current || '',
        draftText: nextDraft,
      });
    }
  }, []);

  const ensureSeqSnapshotOwnership = useCallback(() => {
    if (hasSeqOwnershipSnapshotRef.current) {
      return;
    }
    hasSeqOwnershipSnapshotRef.current = true;
    snapshotCommittedRef.current = committedTextRef.current || '';
    snapshotDraftRef.current = draftTextRef.current || '';
  }, []);

  const shouldSkipSequencedDelta = useCallback((normalizedSeq: number): boolean => {
    if (normalizedSeq <= textLastSeqRef.current) {
      return true;
    }
    return typeof pendingDeltaBySeqRef.current.get(normalizedSeq) === 'string';
  }, []);

  const buildSequencedDeltaState = useCallback(() => {
    const pendingTail = buildPendingTailFromSeq(textLastSeqRef.current);
    const baseCommitted = hasSeqOwnershipSnapshotRef.current
      ? snapshotCommittedRef.current || committedTextRef.current || ''
      : committedTextRef.current || '';
    const baseDraft = hasSeqOwnershipSnapshotRef.current ? snapshotDraftRef.current || '' : draftTextRef.current || '';

    return {
      baseCommitted,
      nextDraft: baseDraft + pendingTail,
    };
  }, [buildPendingTailFromSeq]);

  const applySequencedDelta = useCallback(
    (normalizedSeq: number, delta: string) => {
      if (shouldSkipSequencedDelta(normalizedSeq)) {
        return;
      }

      ensureSeqSnapshotOwnership();
      pendingDeltaBySeqRef.current.set(normalizedSeq, delta);
      const { baseCommitted, nextDraft } = buildSequencedDeltaState();

      emitCommitDraftUpdate(nextDraft);
      const nextRaw = baseCommitted + nextDraft;
      if (nextRaw) {
        applyChannelTranscriptUpdate(nextRaw, 'raw');
        applyTranscriptUpdate(nextRaw, 'raw');
      }
    },
    [
      applyChannelTranscriptUpdate,
      applyTranscriptUpdate,
      buildSequencedDeltaState,
      emitCommitDraftUpdate,
      ensureSeqSnapshotOwnership,
      shouldSkipSequencedDelta,
    ],
  );

  const applyLegacyDelta = useCallback(
    (delta: string) => {
      const nextDraft = (draftTextRef.current || '') + delta;
      emitCommitDraftUpdate(nextDraft);

      const nextRaw = (rawTranscriptRef.current || '') + delta;
      applyChannelTranscriptUpdate(nextRaw, 'raw');
      applyTranscriptUpdate(nextRaw, 'raw');
    },
    [applyChannelTranscriptUpdate, applyTranscriptUpdate, emitCommitDraftUpdate],
  );

  const handleTranscriptDeltaMessage = useCallback(
    (record: UnknownRecord) => {
      const delta = typeof record.delta === 'string' ? record.delta : '';
      const chunkSeq = parseOptionalNumber(readAliased(record, 'chunk_seq', 'chunkSeq'));

      handleSpeech2textInfo(record);
      handlePipelineState(record);

      if (delta.trim().length === 0) {
        return;
      }

      if (chunkSeq !== null) {
        applySequencedDelta(Math.trunc(chunkSeq), delta);
        return;
      }

      applyLegacyDelta(delta);
    },
    [applyLegacyDelta, applySequencedDelta, handlePipelineState, handleSpeech2textInfo],
  );

  const handleWorkspaceUpdatedMessage = useCallback((record: UnknownRecord) => {
    const workspaceId = readWorkspaceId(record) ?? '';
    const updatedMeetingId = readMeetingId(record) ?? '';
    if (!workspaceId || !updatedMeetingId) {
      return;
    }

    const updatedEvent: WorkspaceTranscriptionUpdateEvent = {
      type: 'workspace.transcription.updated',
      workspaceId,
      meetingId: updatedMeetingId,
      status: typeof record.status === 'string' ? record.status : undefined,
      formattedAvailable: readAliasedBoolean(record, 'formatted_available', 'formattedAvailable'),
      minutesStatus: parseMinutesStatus(readAliased(record, 'minutes_status', 'minutesStatus')),
      source: typeof record.source === 'string' ? record.source : undefined,
      tsMs: readTsMs(record),
    };
    onWorkspaceTranscriptionUpdatedRef.current?.(updatedEvent);
  }, []);

  const handleLifecycleMessage = useCallback((messageType: LifecycleMessageType, record: UnknownRecord) => {
    const updatedMeetingId = readMeetingId(record) ?? '';
    if (!updatedMeetingId) {
      return;
    }

    const lifecycleEvent: TranscriptionRealtimeEvent = {
      type: messageType,
      meetingId: updatedMeetingId,
      status: typeof record.status === 'string' ? record.status : undefined,
      formattedAvailable: readAliasedBoolean(record, 'formatted_available', 'formattedAvailable'),
      minutesStatus: parseMinutesStatus(readAliased(record, 'minutes_status', 'minutesStatus')),
      tsMs: readTsMs(record),
    };
    onTranscriptionEventRef.current?.(lifecycleEvent);

    const isTerminalUpdate =
      messageType === 'transcript.finalized' ||
      messageType === 'transcript.formatted' ||
      messageType === 'transcript.completed';

    if (isTerminalUpdate && updatedMeetingId === meetingIdRef.current) {
      setLastDefaultResponseAt(Date.now());
    }
  }, []);

  const handleRecallTranscriptMessage = useCallback((record: UnknownRecord) => {
    const recallMeetingId = readMeetingId(record) ?? '';
    if (!recallMeetingId) {
      return;
    }

    const recallEvent: RecallTranscriptEvent = {
      type: 'recall.transcript',
      meetingId: recallMeetingId,
      speaker: readAliasedString(record, 'speaker', 'speaker') ?? '',
      text: readAliasedString(record, 'text', 'text') ?? '',
      isFinal: readAliasedBoolean(record, 'is_final', 'isFinal') ?? false,
      tsMs: readTsMs(record) ?? Date.now(),
    };
    onRecallTranscriptReceivedRef.current?.(recallEvent);
  }, []);

  const handleRecallBotStatusMessage = useCallback((record: UnknownRecord) => {
    const recallMeetingId = readMeetingId(record) ?? '';
    if (!recallMeetingId) {
      return;
    }

    const statusEvent: RecallBotStatusEvent = {
      type: 'recall.bot_status',
      meetingId: recallMeetingId,
      botId: readAliasedString(record, 'bot_id', 'botId') ?? '',
      status: readAliasedString(record, 'status', 'status') ?? '',
      recordingState: readAliasedString(record, 'recording_state', 'recordingState') ?? '',
      tsMs: readTsMs(record) ?? Date.now(),
    };
    onRecallBotStatusChangedRef.current?.(statusEvent);
  }, []);

  const parseStatusCodeResponseBody = useCallback((responseBody: { body?: unknown }): UnknownRecord => {
    return typeof responseBody.body === 'string'
      ? (JSON.parse(responseBody.body) as UnknownRecord)
      : ((responseBody.body as UnknownRecord) ?? {});
  }, []);

  const logStatusCodeResponseWarning = useCallback((responseBody: { statusCode?: number; body?: unknown }) => {
    if (responseBody.statusCode === 404) {
      logger.debug('[useTranscriptionConnection] Transcript not yet available (404)');
      return;
    }

    if (typeof responseBody.statusCode === 'number' && responseBody.statusCode >= 400) {
      logger.warn('[useTranscriptionConnection] $default error response', {
        statusCode: responseBody.statusCode,
        body: responseBody.body,
      });
    }
  }, []);

  const handleStatusCodeResponse = useCallback(
    (responseBody: { statusCode?: number; body?: unknown }) => {
      try {
        if (responseBody.statusCode === 200 && responseBody.body) {
          setLastDefaultResponseAt(Date.now());
          handleSnapshotBody(parseStatusCodeResponseBody(responseBody));
        } else {
          logStatusCodeResponseWarning(responseBody);
        }
      } catch (parseError) {
        logger.warn('[useTranscriptionConnection] Failed to parse $default response', {
          error: parseError,
        });
      }
    },
    [handleSnapshotBody, logStatusCodeResponseWarning, parseStatusCodeResponseBody],
  );

  const handlePotentialResponseMessage = useCallback(
    (message: WebSocketMessage) => {
      logger.debug('[useTranscriptionConnection] Processing potential $default response', {
        type: message.type,
      });

      const responseRecord = message as unknown as UnknownRecord;
      if ('statusCode' in responseRecord && 'body' in responseRecord) {
        handleStatusCodeResponse(message as unknown as { statusCode?: number; body?: unknown });
        return;
      }

      const bodyCandidate = message as unknown as UnknownRecord;
      if (isSnapshotCandidate(bodyCandidate)) {
        handleSnapshotBody(bodyCandidate);
      }
    },
    [handleSnapshotBody, handleStatusCodeResponse],
  );

  const dispatchTypedRealtimeMessage = useCallback(
    (messageType: string, record: UnknownRecord): boolean => {
      const handlers: Record<string, (() => void) | undefined> = {
        'workspace.transcription.updated': () => handleWorkspaceUpdatedMessage(record),
      }
      const handler = handlers[messageType];
      if (handler) {
        handler();
        return true;
      }
      if (isLifecycleMessageType(messageType)) {
        handleLifecycleMessage(messageType, record);
        return true;
      }
      return false;
    },
    [handleLifecycleMessage, handleWorkspaceUpdatedMessage],
  );

  const dispatchUntypedRealtimeMessage = useCallback(
    (messageType: WebSocketMessage['type'], record: UnknownRecord): boolean => {
      const handlers: Record<string, (() => void) | undefined> = {
        'transcript.delta': () => handleTranscriptDeltaMessage(record),
        'transcript.snapshot': () => handleSnapshotBody(record),
        'recall.transcript': () => handleRecallTranscriptMessage(record),
        'recall.bot_status': () => handleRecallBotStatusMessage(record),
      };
      if (!messageType) {
        return false;
      }
      const handler = handlers[messageType];
      if (!handler) {
        return false;
      }
      handler();
      return true;
    },
    [
      handleRecallBotStatusMessage,
      handleRecallTranscriptMessage,
      handleSnapshotBody,
      handleTranscriptDeltaMessage,
    ],
  );

  const dispatchRealtimeMessage = useCallback(
    (messageType: string, message: WebSocketMessage, record: UnknownRecord): boolean => {
      return dispatchTypedRealtimeMessage(messageType, record) || dispatchUntypedRealtimeMessage(message.type, record);
    },
    [dispatchTypedRealtimeMessage, dispatchUntypedRealtimeMessage],
  );

  const handleNonRealtimeMessage = useCallback(
    (message: WebSocketMessage): boolean => {
      if (message.type === 'transcript' && message.text) {
        applyTranscriptUpdate(message.text, 'legacy');
        return true;
      }
      if (message.type === 'response' || !message.type) {
        handlePotentialResponseMessage(message);
        return true;
      }
      if (message.type !== 'error' || !message.error) {
        return false;
      }

      const err = new Error(message.error);
      reportError(err, 'error');
      logger.error('[useTranscriptionConnection] Error received', {
        error: message.error,
      });
      return true;
    },
    [applyTranscriptUpdate, handlePotentialResponseMessage, reportError],
  );

  const handleIncomingMessage = useCallback(
    (message: WebSocketMessage) => {
      logger.debug('[useTranscriptionConnection] Message received', { message });

      const messageType = typeof message.type === 'string' ? message.type : '';
      const record = message as unknown as UnknownRecord;
      if (dispatchRealtimeMessage(messageType, message, record)) {
        return;
      }
      handleNonRealtimeMessage(message);
    },
    [dispatchRealtimeMessage, handleNonRealtimeMessage],
  );

  const handleSocketMessage = useCallback(
    async (event: MessageEvent) => {
      try {
        const payloadText = await readSocketPayloadText(event.data);
        const message = JSON.parse(payloadText) as WebSocketMessage;
        handleIncomingMessage(message);
      } catch (err) {
        logger.error('[useTranscriptionConnection] Failed to parse message', { error: err });
      }
    },
    [handleIncomingMessage],
  );

  const fetchWebSocketToken = useCallback(async (): Promise<string> => {
    const response = await fetch('/api/auth/websocket-token');
    if (!response.ok) {
      throw new HttpStatusError(`Failed to get WebSocket token: ${response.status}`, response.status);
    }

    const data = await response.json();
    return data.data.token;
  }, []);

  const resolveBaseWebSocketUrl = useCallback((): string => {
    const baseUrl = wsUrl || process.env.NEXT_PUBLIC_TRANSCRIPTION_WS_URL;
    if (!baseUrl) {
      throw new Error('WebSocket URL not configured');
    }
    return baseUrl;
  }, [wsUrl]);

  const prepareConnectionState = useCallback((params?: ConnectParams) => {
    const resolvedParams = resolveConnectOptions(params);
    meetingIdRef.current = resolvedParams.generatedMeetingId;
    setMeetingId(resolvedParams.generatedMeetingId);
    transcriptRef.current = '';
    setTranscript('');
    setConnectionState('connecting');

    logger.info('[useTranscriptionConnection] Preparing connection', {
      meetingId: resolvedParams.generatedMeetingId,
      workspaceId: resolvedParams.providedWorkspaceId || null,
      connectionOnly: resolvedParams.connectionOnly,
      resumeRequested: resolvedParams.resumeRequested,
    });

    return resolvedParams;
  }, []);

  const openWebSocketConnection = useCallback(
    async (
      baseUrl: string,
      params: ReturnType<typeof resolveConnectOptions>,
      resolve: (value: string | PromiseLike<string>) => void,
      reject: (reason?: unknown) => void,
    ) => {
      const token = await fetchWebSocketToken();
      const query = new URLSearchParams({ token });
      if (params.generatedMeetingId) {
        query.set('meetingId', params.generatedMeetingId);
      }
      // 一覧の更新監視だけを目的とする接続には meetingId が無い。speech2text の $connect は
      // meetingId も connection_only も無い接続を「録音接続なのに meeting_id が欠けている」
      // とみなして 400 を返すため、このフラグを載せないと handshake が必ず失敗する。
      if (params.connectionOnly) {
        query.set('connection_only', '1');
      }
      if (params.providedWorkspaceId) {
        query.set('workspaceId', params.providedWorkspaceId);
      }
      if (params.resumeRequested && params.generatedMeetingId) {
        query.set('resume', '1');
      }

      logger.info('[useTranscriptionConnection] Connecting with auth token', {
        meetingId: params.generatedMeetingId,
        workspaceId: params.providedWorkspaceId || null,
        connectionOnly: params.connectionOnly,
        resumeRequested: params.resumeRequested,
      });

      const ws = new WebSocket(`${baseUrl}?${query.toString()}`);
      wsRef.current = ws;
      // onopen が一度でも走ったかを追跡。onclose 時点で false なら connect 中の異常終了。
      // WHATWG 仕様上 onclose 時の readyState は常に CLOSED になるため readyState 依存は不可。
      let hasOpened = false;

      ws.onopen = () => {
        logger.info('[useTranscriptionConnection] WebSocket connected', {
          meetingId: params.generatedMeetingId,
          workspaceId: params.providedWorkspaceId || null,
          connectionOnly: params.connectionOnly,
        });

        hasOpened = true;
        setConnectionId(params.resolvedConnectionId);
        setConnectionState('connected');
        setError(null);

        if (params.generatedMeetingId) {
          onConnectedRef.current?.(params.resolvedConnectionId, params.generatedMeetingId);
        }
        resolve(params.resolvedConnectionId);
      };

      ws.onmessage = handleSocketMessage;
      ws.onerror = (event) => {
        const err = new Error('WebSocket error occurred');
        reportError(err, 'error');
        logger.error('[useTranscriptionConnection] WebSocket error', { event });
        reject(err);
      };
      ws.onclose = (event) => {
        const wasConnecting = !hasOpened;

        setConnectionState('disconnected');
        setConnectionId(null);
        wsRef.current = null;
        logger.info('[useTranscriptionConnection] Disconnected', {
          code: event.code,
          reason: event.reason,
          wasClean: event.wasClean,
        });
        onDisconnectedRef.current?.();
        onCloseRef.current?.({
          code: event.code,
          reason: event.reason,
          wasClean: event.wasClean,
        });

        if (wasConnecting) {
          reject(new Error(`Connection closed: ${event.code} - ${event.reason || 'Unknown reason'}`));
        }
      };
    },
    [fetchWebSocketToken, handleSocketMessage, reportError],
  );

  /**
   * 同期的に `action: 'final'` を WS に enqueue する。
   * 画面遷移によるアンマウントなど、非同期 state machine 遷移を待てない瞬間に
   * 呼ぶことで、close より前に finalize リクエストをサーバへ届ける。
   *
   * - readyState !== OPEN の場合は何もしない（CONNECTING/CLOSING/CLOSED）。
   * - meetingIdRef.current が null（connectionOnly モード等）の場合は対象外。
   * - finalSentRef で同一接続内での二重送信を防ぐ。
   */
  const sendFinalSync = useCallback((): boolean => {
    const ws = wsRef.current;
    const currentMeetingId = meetingIdRef.current;
    const eligible =
      !!ws &&
      ws.readyState === WebSocket.OPEN &&
      !finalSentRef.current &&
      !!currentMeetingId;
    if (!eligible || !ws || !currentMeetingId) {
      return false;
    }
    try {
      const payload: WebSocketMessage = {
        action: 'final',
        connectionId: connectionId || undefined,
        meeting_id: currentMeetingId,
      };
      ws.send(JSON.stringify(payload));
      finalSentRef.current = true;
      logger.info('[useTranscriptionConnection] Final sent synchronously before close', {
        meetingId: currentMeetingId,
      });
      return true;
    } catch (err) {
      logger.warn('[useTranscriptionConnection] Failed to send final synchronously', {
        error: err instanceof Error ? err.message : String(err),
      });
      return false;
    }
  }, [connectionId]);

  // disconnect / unmount cleanup から最新の sendFinalSync を呼べるよう ref に保持する。
  // useEffect/useCallback の依存配列に sendFinalSync を入れると connectionId 変化の
  // たびに不要な再実行が発生するため、ref 経由で最新参照だけを取り出す。
  const sendFinalSyncRef = useRef(sendFinalSync);
  useEffect(() => {
    sendFinalSyncRef.current = sendFinalSync;
  }, [sendFinalSync]);

  /**
   * WebSocket接続を開始
   */
  const connect = useCallback(
    async (params?: ConnectParams): Promise<string> => {
      return new Promise((resolve, reject) => {
        try {
          if (wsRef.current) {
            reject(new Error('Already connected or connecting'));
            return;
          }

          // 新しい接続では final 未送信状態に戻す。
          finalSentRef.current = false;
          resetTranscriptState();
          setError(null);
          const baseUrl = resolveBaseWebSocketUrl();
          const resolvedParams = prepareConnectionState(params);
          void openWebSocketConnection(baseUrl, resolvedParams, resolve, reject).catch((err) => {
            const error = err instanceof Error ? err : new Error(String(err));
            reportError(error, 'error');
            // 401 はログイン失効で再試行しても回復しない。error で記録すると Sentry へ送られ、
            // 失効したタブが残る間イベントが増え続けるため warn に留める（呼び出し側で再試行も止める）。
            if (isUnauthorizedError(error)) {
              logger.warn('[useTranscriptionConnection] WebSocket token unauthorized', { error: error.message });
            } else {
              logger.error('[useTranscriptionConnection] Failed to get token or connect', { error });
            }
            reject(error);
          });
        } catch (err) {
          const error = err instanceof Error ? err : new Error(String(err));
          reportError(error, 'error');
          logger.error('[useTranscriptionConnection] Connection failed', { error });
          reject(error);
        }
      });
    },
    [openWebSocketConnection, prepareConnectionState, reportError, resetTranscriptState, resolveBaseWebSocketUrl],
  );

  /**
   * WebSocket接続を切断
   *
   * sendFinalSync を ref 経由で参照することで、`disconnect` 自体は connectionId
   * 変化の影響を受けない安定参照を保つ。これにより `useEffect(..., [disconnect])`
   * を持つ呼び出し側 (例: RecordButton.tsx の connecting effect) が意図せず
   * 再実行される事故を防ぐ。
   */
  const disconnect = useCallback(() => {
    if (wsRef.current) {
      // 明示停止フローでも、サーバ側に finalize を確実に届けてから close する。
      sendFinalSyncRef.current?.();
      wsRef.current.close();
      wsRef.current = null;
      setConnectionId(null);
      setMeetingId(null);
      meetingIdRef.current = null;
      setConnectionState('disconnected');
      resetTranscriptState();
      logger.info('[useTranscriptionConnection] Disconnected manually');
    }
  }, [resetTranscriptState]);

  const getOpenSocket = useCallback((): WebSocket => {
    if (!wsRef.current) {
      throw new Error('Not connected');
    }
    if (wsRef.current.readyState !== WebSocket.OPEN) {
      throw new Error('WebSocket is not open');
    }
    return wsRef.current;
  }, []);

  const getCurrentMeetingId = useCallback((): string => {
    const currentMeetingId = meetingIdRef.current || meetingId;
    if (!currentMeetingId) {
      throw new Error('meetingId is not available - connection may not be fully established');
    }
    return currentMeetingId;
  }, [meetingId]);

  const sendSocketAction = useCallback(
    (action: string, data?: Record<string, unknown>): string => {
      const ws = getOpenSocket();
      const currentMeetingId = getCurrentMeetingId();
      const payload: WebSocketMessage = {
        action,
        connectionId: connectionId || undefined,
        meeting_id: currentMeetingId,
        ...(data ? { data } : {}),
      };
      const messageStr = JSON.stringify(payload);
      ws.send(messageStr);
      return messageStr;
    },
    [connectionId, getCurrentMeetingId, getOpenSocket],
  );

  const reportSendError = useCallback((scope: string, error: unknown, rethrow = true) => {
    const resolvedError = error instanceof Error ? error : new Error(String(error));
    setError(resolvedError);
    logger.error(`[useTranscriptionConnection] Failed to ${scope}`, { error: resolvedError });
    onErrorRef.current?.(resolvedError);
    if (rethrow) {
      throw resolvedError;
    }
  }, []);

  const buildChunkMessagePayload = useCallback(
    (chunk: Blob, currentMeetingId: string, base64Audio: string, chunkSeq?: number): WebSocketMessage => {
      const audioMetadata = getAudioChunkMetadata(chunk.type);
      const pcmData = buildChunkPcmData(audioMetadata);
      return {
        action: 'transcribe_chunk',
        connectionId: connectionId || undefined,
        meeting_id: currentMeetingId,
        data: {
          audioData: base64Audio,
          contentType: chunk.type,
          ...pcmData,
          ...(typeof chunkSeq === 'number' ? { chunk_seq: chunkSeq } : {}),
        },
      };
    },
    [connectionId],
  );

  const logChunkPayloadSize = useCallback((base64Audio: string, messageStr: string) => {
    const messageSizeBytes = new TextEncoder().encode(messageStr).length;
    const SIZE_WARN_THRESHOLD = 30 * 1024;
    if (messageSizeBytes <= SIZE_WARN_THRESHOLD) {
      return;
    }

    logger.warn('[useTranscriptionConnection] Message size approaching 32KB limit', {
      messageSizeBytes,
      messageSizeKB: (messageSizeBytes / 1024).toFixed(2),
      base64Length: base64Audio.length,
    });
  }, []);

  /**
   * 音声チャンクを送信
   */
  const sendChunk = useCallback(
    async (chunk: Blob, chunkSeq?: number): Promise<void> => {
      try {
        const ws = getOpenSocket();
        const currentMeetingId = getCurrentMeetingId();
        const audioMetadata = getAudioChunkMetadata(chunk.type);
        logger.info('[useTranscriptionConnection] Sending chunk', {
          chunkSize: chunk.size,
          chunkType: chunk.type,
          detectedFormat: audioMetadata.detectedFormat,
        });

        if (chunk.size === 0) {
          throw new Error('Audio chunk is empty (size: 0)');
        }

        const base64Audio = await blobToBase64(chunk);
        const messagePayload = buildChunkMessagePayload(chunk, currentMeetingId, base64Audio, chunkSeq);
        const messageStr = JSON.stringify(messagePayload);
        logChunkPayloadSize(base64Audio, messageStr);

        logger.info('[useTranscriptionConnection] Sending WebSocket message', {
          action: 'transcribe_chunk',
          hasMeetingId: !!currentMeetingId,
          meetingId: currentMeetingId,
          hasConnectionId: !!connectionId,
          base64Length: base64Audio.length,
          contentType: chunk.type,
          detectedFormat: audioMetadata.detectedFormat,
        });

        ws.send(messageStr);
        setLastChunkSentAt(Date.now());

        logger.debug('[useTranscriptionConnection] Chunk sent', {
          chunkSize: chunk.size,
          connectionId,
        });
      } catch (err) {
        reportSendError('send chunk', err);
      }
    },
    [buildChunkMessagePayload, connectionId, getCurrentMeetingId, getOpenSocket, logChunkPayloadSize, reportSendError],
  );

  /**
   * フォーマット要求を送信
   */
  const requestFormat = useCallback(async (): Promise<void> => {
    try {
      sendSocketAction('format');
      logger.info('[useTranscriptionConnection] Format requested', { connectionId });
    } catch (err) {
      reportSendError('request format', err);
    }
  }, [connectionId, reportSendError, sendSocketAction]);

  /**
   * $final route経由でS3に文字起こしを保存
   */
  const requestFinal = useCallback(async (): Promise<void> => {
    try {
      sendSocketAction('final');
      logger.info('[useTranscriptionConnection] Final requested', { connectionId });
    } catch (err) {
      reportSendError('request final', err);
    }
  }, [connectionId, reportSendError, sendSocketAction]);

  /**
   * $default route経由で文字起こし結果を取得
   */
  const requestTranscript = useCallback(async (): Promise<void> => {
    try {
      const currentMeetingId = getCurrentMeetingId();
      sendSocketAction('default');
      logger.debug('[useTranscriptionConnection] Transcript requested', {
        connectionId,
        meetingId: currentMeetingId,
      });
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      logger.warn('[useTranscriptionConnection] Failed to request transcript', { error });
    }
  }, [connectionId, getCurrentMeetingId, sendSocketAction]);

  /**
   * ポーリングを開始（指定間隔で $default route を呼び出し）
   */
  const startPolling = useCallback(() => {
    // 既にポーリング中の場合はスキップ
    if (isPollingRef.current) {
      logger.debug('[useTranscriptionConnection] Polling already started');
      return;
    }

    isPollingRef.current = true;

    const POLLING_INTERVAL_MS = 2000;

    logger.info('[useTranscriptionConnection] Starting polling', {
      interval: `${POLLING_INTERVAL_MS}ms`,
    });

    // 即座に1回実行
    requestTranscript().catch((err) => {
      logger.warn('[useTranscriptionConnection] Initial transcript request failed', { error: err });
    });

    // 指定間隔でポーリング
    pollingIntervalRef.current = setInterval(() => {
      requestTranscript().catch((err) => {
        logger.warn('[useTranscriptionConnection] Polling transcript request failed', { error: err });
      });
    }, POLLING_INTERVAL_MS);
  }, [requestTranscript]);

  /**
   * ポーリングを停止
   */
  const stopPolling = useCallback(() => {
    if (!isPollingRef.current) {
      logger.debug('[useTranscriptionConnection] Polling not started');
      return;
    }

    isPollingRef.current = false;

    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
      logger.info('[useTranscriptionConnection] Polling stopped');
    }
  }, []);

  /**
   * アンマウント時のクリーンアップ
   */
  useEffect(() => {
    return () => {
      // ポーリング停止
      stopPolling();

      // WebSocket クローズ
      if (wsRef.current) {
        // close する前に同期 send で finalize を届ける。
        // connectionOnly モードでは meetingIdRef.current が null のため
        // sendFinalSync は no-op となる（明示分岐は不要）。
        sendFinalSyncRef.current?.();
        wsRef.current.close();
        wsRef.current = null;
        logger.info('[useTranscriptionConnection] Cleanup on unmount');
      }
    };
  }, [stopPolling]);

  return {
    connect,
    disconnect,
    sendChunk,
    requestFormat,
    requestFinal,
    requestTranscript,
    startPolling,
    stopPolling,
    connectionState,
    transcript,
    error,
    connectionId,
    meetingId,
    lastChunkSentAt,
    lastTranscriptAt,
    lastDefaultResponseAt,
    rawChannelText,
    formattedChannelText,
    committedText,
    draftText,
    revision,
    pipelineState,
  };
}
